import {Component, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import * as fromStore from "../../store";
import {Subject, timer} from "rxjs";
import {ActivatedRoute} from "@angular/router";
import {IFcDropdownSelectOption} from "../../shared/components/form-controls/dropdown-select/fc-dropdown-select-option";
import {ActivityNotificationService} from "../../activity-notifications/activity-notification.service";
import {DeletedDomainActivity, DeletedUserActivity, UserActivity} from "./user-activity.model";
import activityLogActionsList from "./activity-log-actions-list.json";
import {TypeUtils} from "../../shared/utils/type-utils";
import {filter, switchMap, takeUntil} from "rxjs/operators";
import {Website} from "../../websites/website/website";
import {User} from "../../user/user";
import {Utils} from "../../shared/utils/utils";
import features from "./activity-log-features.json";
import {UserSubscription} from "../../user/user-subscription";

@Component({
    selector: 'db-activity-log',
    templateUrl: './activity-log.component.html',
    styleUrls: ['./activity-log.component.scss'],
    standalone: false
})
export class ActivityLogComponent implements OnInit, OnDestroy {

  public user: User;
  public userSubscription: UserSubscription;
  public activities: any;
  public deletedDomainsActivities: any;
  public deletedClientsActivities: any;
  public workspaceClients: any[] = [];
  public filteredActivities: any;
  public queryParams: any;
  public clientsList: Array<IFcDropdownSelectOption>;
  public actionsList: any = activityLogActionsList;
  public getWebsiteSiblingIds: any;
  public features: string[] = features;
  public demoUser: boolean = false;
  private _destroyStream: Subject<void> = new Subject<void>();

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _route: ActivatedRoute,
    private _activityNotificationService: ActivityNotificationService
  ) {}

  ngOnInit() {
    this.user = this._route.snapshot.data['user'];
    this.userSubscription = this._route.snapshot.data['subscription'];
    this.activities = this._route.snapshot.data['activities'];
    this.deletedClientsActivities = this._route.snapshot.data['deletedClients'];
    this.deletedDomainsActivities = this._route.snapshot.data['deletedDomains'];
    this.workspaceClients = this._route.snapshot.data['workspace'].workspaceClients;
    this.getClients();
    this.queryParams = (<any>this._route.queryParams).value;
    this.filterActivityLog(this.queryParams);
    this.getUsersActivitiesSnapshot();
    this.loadDeletedActivities();
    this.getAiBuilderDemoUser();
  }

  public getAiBuilderDemoUser() {
    this.demoUser = Utils.isDemoUser(this.userSubscription, this.user);
  }

  public getClients(): void {
    this.clientsList = [
      {
        "label": 'All users',
        "value": 'all'
      },
      {
        "label": this.user.firstName + ' ' + this.user.lastName,
        "value": this.user.email,
        "description": this.user.email
      }
    ];
    this._store.select(fromStore.getWorkspaceLoaded)
      .pipe(
        takeUntil(this._destroyStream),
        filter(loaded => loaded),
        switchMap(() => this._store.select(fromStore.getWorkspaceAllClients))
      )
      .subscribe( (clients) => {
        this.workspaceClients = clients.filter(client => client.status);
        Object.keys(this.workspaceClients).forEach((key: string): void => {
          this.clientsList.push({
            "label": TypeUtils.isDefined(this.workspaceClients[key].name) ? this.workspaceClients[key].name : '',
            "value": this.workspaceClients[key].email,
            "description": this.workspaceClients[key].email
          });
        });
      });
    this.clientsList.concat(this.deletedClientsActivities);
  }


  public filterActivityLog(filter): void {
    if (TypeUtils.isDefined(filter.startDate) && TypeUtils.isDefined(filter.endDate)) {
      const dateFilter: [number, number] = [parseInt(filter.startDate, 10), parseInt(filter.endDate, 10)];
      this._activityNotificationService.getUsersActivities(dateFilter)
        .then(activities => {
          const activitiesLog = activities.map(activity => new UserActivity(activity));
          if (Object.keys(filter).length > 1) {
            this.filtering(activitiesLog, filter);
          }
          else {
            this.filteredActivities = activitiesLog;
          }
        });
    }
    else {
      this.filtering(this.activities, filter);
    }
  }

  private getUsersActivitiesSnapshot() {
    /*activities && activities.length < 2 because first snapshot is whole activity log*/
    this._activityNotificationService.getUsersActivitiesSnapshot()
      .pipe(
        takeUntil(this._destroyStream),
        filter((activities: UserActivity[]) => {
          return activities && activities.length < 2 && !Utils.deepEqual(activities[0], this.activities[0]);
        })
      )
      .subscribe(activities => {
        this.activities = activities.concat(this.activities);
        this.filteredActivities = Object.keys(filter).length > 1 ? this.filtering(this.activities, filter) : this.activities;
      });
  }

  private loadDeletedActivities(): void {
    // If we already have data from resolvers and they're not empty, no need to reload
    if (this.deletedClientsActivities?.length > 0 && this.deletedDomainsActivities?.length > 0) {
      return;
    }

    // Wait for workspace to be loaded and Firebase to be ready
    this._store.select(fromStore.getWorkspaceIdEntities)
      .pipe(
        takeUntil(this._destroyStream),
        filter(workspaceIdEntities => workspaceIdEntities?.currentWorkspaceId != null),
        switchMap(() => {
          // Start polling for Firebase readiness after workspace is available
          return timer(2000, 3000); // Start after 2s, then poll every 3s
        }),
        takeUntil(this._destroyStream)
      )
      .subscribe(() => {
        this.tryLoadDeletedActivities();
      });
  }

  private tryLoadDeletedActivities(): void {
    // Try to load deleted clients if we don't have them
    if (!this.deletedClientsActivities?.length) {
      this._activityNotificationService.getDeletedClientsActivities()
        .then(activities => {
          if (activities?.length > 0) {
            this.deletedClientsActivities = activities.map(activity => new DeletedUserActivity(activity));
            this.updateClientsList();
          }
        })
        .catch(() => {
          // Silently fail - will retry on next poll
        });
    }

    // Try to load deleted domains if we don't have them
    if (!this.deletedDomainsActivities?.length) {
      this._activityNotificationService.getDeletedDomainsActivities()
        .then(activities => {
          if (activities?.length > 0) {
            this.deletedDomainsActivities = activities.map(activity => new DeletedDomainActivity(activity));
          }
        })
        .catch(() => {
          // Silently fail - will retry on next poll
        });
    }
  }

  private updateClientsList(): void {
    // Add deleted clients to the clients list if they were loaded
    if (this.deletedClientsActivities?.length > 0) {
      this.deletedClientsActivities.forEach(deletedClient => {
        // Check if not already in the list
        if (!this.clientsList.find(client => client.value === deletedClient.email)) {
          this.clientsList.push({
            label: deletedClient.firstName ? `${deletedClient.firstName} (deleted)` : 'Deleted User',
            value: deletedClient.email,
            description: `${deletedClient.email} (deleted)`
          });
        }
      });
    }
  }

  public filtering(data, filter): any {
    if (TypeUtils.isDefined(filter.type) && filter.type === 'website' && TypeUtils.isDefined(filter.websiteId)
      && TypeUtils.isDefined(filter.websiteType)) {
      this._store.select(fromStore.getWebsite(filter.websiteId)).subscribe((website: Website) => {
        this.getWebsiteSiblingIds = website.siblingId;
        const newData = data.filter(item => (this.getWebsiteSiblingIds
          ? (filter.websiteType === 'staging' ? this.getWebsiteSiblingIds.dev === item.websiteId : this.getWebsiteSiblingIds.live === item.websiteId) : true));
        this.filteredActivities = this.filterData(newData, filter, filter.websiteType === 'staging' ? this.getWebsiteSiblingIds.dev : this.getWebsiteSiblingIds.live);
      })
    }
    else {
      this.filteredActivities = this.filterData(data, filter);
    }
  }

  public filterData(data, _filter, siblingId?): [] {
    return data.filter(item => {
      return (TypeUtils.isDefined(_filter.userEmail) ? item.userEmail === _filter.userEmail : true)
        && (TypeUtils.isDefined(_filter.type) ? (_filter.type === 'payment_info' ? item.action.includes('merchant') : this.actionsList[_filter.type].includes(item.action)) : true)
        && (TypeUtils.isDefined(_filter.type) && _filter.type === 'website' && TypeUtils.isDefined(_filter.websiteId)
          ? [item.websiteId, item.websiteParentId].includes(parseInt(siblingId ? siblingId : _filter.websiteId, 10)) : true)
    });
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
  }

}
